import type {
  IntensityMetricTypeEnum,
  PriorYearSourceEnum,
} from '@shared/api/types';
import {
  IntensityMetricTypeEnum as IntensityMetric,
  PriorYearSourceEnum as PriorYearSource,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import React from 'react';
import { type ScopeCategorySlug } from '../slugs';
import { type EmissionRowKey } from './schema';

export const INTENSITY_METRIC_OPTIONS: {
  value: IntensityMetricTypeEnum;
  label: string;
  unit: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: IntensityMetric.revenue,
    label: 'Revenue',
    unit: '£m',
    description: 'Total annual revenue in millions of pounds',
    icon: <Icons.banknote className="size-4 shrink-0 text-muted-foreground" />,
  },
  {
    value: IntensityMetric.floor_area,
    label: 'Floor space',
    unit: 'm²',
    description: 'Total occupied floor area in square metres',
    icon: <Icons.building2 className="size-4 shrink-0 text-muted-foreground" />,
  },
  {
    value: IntensityMetric.employees,
    label: 'Headcount',
    unit: 'employee',
    description: 'Average number of full-time equivalent employees',
    icon: <Icons.users className="size-4 shrink-0 text-muted-foreground" />,
  },
  {
    value: IntensityMetric.production_output,
    label: 'Production volume',
    unit: 'tonne',
    description: 'Total production output in tonnes over the period',
    icon: <Icons.package className="size-4 shrink-0 text-muted-foreground" />,
  },
];

export const PREVIOUS_SOURCE_OPTIONS: {
  value: PriorYearSourceEnum;
  label: string;
}[] = [
  { value: PriorYearSource.none, label: 'No, this is our first report' },
  { value: PriorYearSource.my_emissions, label: 'Yes, with My Emissions' },
  {
    value: PriorYearSource.off_platform,
    label: 'Yes, but not with My Emissions',
  },
];

export const EMISSION_SCOPES: {
  label: string;
  rows: {
    key: EmissionRowKey;
    slug: ScopeCategorySlug;
    label: string;
    icon: React.ReactNode;
  }[];
}[] = [
  {
    label: 'Scope 1 — Direct emissions',
    rows: [
      {
        key: 'stationaryCombustion',
        slug: 'stationary-combustion',
        label: 'Stationary combustion',
        icon: <Icons.flame className="size-3.5 text-orange-400" />,
      },
      {
        key: 'mobileCombustion',
        slug: 'mobile-combustion',
        label: 'Mobile combustion',
        icon: <Icons.truck className="size-3.5 text-orange-400" />,
      },
    ],
  },
  {
    label: 'Scope 2 — Indirect energy',
    rows: [
      {
        key: 'electricity',
        slug: 'electricity',
        label: 'Electricity (location-based)',
        icon: <Icons.zap className="size-3.5 text-orange-400" />,
      },
      {
        key: 'heat',
        slug: 'heat',
        label: 'Heating and steam',
        icon: <Icons.thermometer className="size-3.5 text-orange-400" />,
      },
    ],
  },
  {
    label: 'Scope 3 — Value chain emissions',
    rows: [
      {
        key: 'businessTravel',
        slug: 'business-travel',
        label: 'Business travel',
        icon: <Icons.plane className="size-3.5 text-orange-400" />,
      },
    ],
  },
];

export const ALL_EMISSION_KEYS: EmissionRowKey[] = EMISSION_SCOPES.flatMap(
  (s) => s.rows.map((r) => r.key)
);

export const TABLE_INPUT_CLASS =
  'rounded-none border-0 bg-transparent px-3 py-2.5 text-right text-sm shadow-none placeholder:text-muted-foreground/40 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
